/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

import PurchaseOrderIcon from './../../assets/images/purchase_order_icon.svg';
import TransferOrderIcon from './../../assets/images/transfer_order_icon.svg';
import PendingOrderIcon from './../../assets/images/purchase_order_pending_icon.svg';
import { LoadDashBoardStats, LoadDashBoardFeeds } from '../../features/General/GeneralSlice';
import {
  LoadDashBoardPendingPOs,
  LoadDashBoardFeedbackPOs,
  LoadDashBoardOpenPOs
} from '../../features/Order/OrderSlice';
import { loaderActions } from '../../features/Loader/LoaderSlice';
import Feed from '../../components/shared/Feed/Index.jsx';
import { Link } from 'react-router-dom';

import './dashboard.scss';
import { VendorToCompanyID } from '../../components/shared/Company';
import { buildPurchaseOrdersFilter } from '../../Helpers/purchaseOrderFilters';

function Dashboard() {
  const dispatch = useDispatch();
  const { currentVendor } = useSelector((state) => state.vendor);
  const { role, can_approve_po } = useSelector((state) => state.auth);
  const { pendingPos, pendingFeedbackLines, openPoCount } = useSelector((state) => state.order);
  const { TOCount, chart, feeds, isFeedBackLoopEnabled } = useSelector((state) => state.general);
  const [chartData, setChartData] = useState([]);
  const showFeedbackSection =
    (role === 'admin' ||
      role === 'supervisor' ||
      role === 'manager' ||
      role === 'team-lead' ||
      role === 'senior-team-lead') &&
    isFeedBackLoopEnabled === true;

  async function fetchDashboardData() {
    const companyId = VendorToCompanyID(currentVendor);
    const isWarehouse = currentVendor.VendorType.toLowerCase() === 'warehouse';
    const pendingPosFilter = isWarehouse
      ? `whseVendorCode eq '${currentVendor.vendNo}' and vendorPortalStatus eq 'Confirmation_x0020_Sent_x0020_to_x0020_Vendor'`
      : `vendorNo eq '${currentVendor.vendNo}' and vendorPortalStatus eq 'Confirmation_x0020_Sent_x0020_to_x0020_Vendor'`;

    await dispatch(loaderActions.show());

    // These requests are independent of each other, so fire them all in parallel
    // instead of awaiting them one at a time.
    const requests = [
      dispatch(LoadDashBoardStats({ companyId, vendNo: currentVendor.vendNo })),
      dispatch(
        LoadDashBoardOpenPOs({ companyId, filter: buildPurchaseOrdersFilter(currentVendor) })
      ),
      dispatch(LoadDashBoardPendingPOs({ companyId, filter: pendingPosFilter }))
    ];

    if (isWarehouse && isFeedBackLoopEnabled === true) {
      requests.push(
        dispatch(
          LoadDashBoardFeedbackPOs({
            companyId,
            filter: `feedbackStatus eq 'Pending' and whseVendorCode eq '${currentVendor.vendNo}'`
          })
        )
      );
    }

    if (process.env.REACT_APP_ENV === 'dev') {
      requests.push(dispatch(LoadDashBoardFeeds({ companyId, vendNo: currentVendor.vendNo })));
    }

    await Promise.all(requests);
    await dispatch(loaderActions.hide());
  }

  useEffect(() => {
    if (currentVendor.vendNo) {
      fetchDashboardData();
    }
  }, [currentVendor]);

  useEffect(() => {
    setChartData(chart);
  }, [chart]);

  const feedsContent = feeds.map((feed, index) => {
    return <Feed {...feed} key={index} VendorType={currentVendor.VendorType} />;
  });

  return (
    <>
      <div className="row">
        <div
          className={
            currentVendor.VendorType === 'Warehouse'
              ? showFeedbackSection
                ? 'col-xl-3 col-md-3'
                : 'col-xl-4 col-md-4'
              : 'col-xl-6 col-md-6'
          }
        >
          <Link to={'/purchase-orders?range=none'}>
            <div className="card prod-p-card bg-c-blue">
              <div className="card-body dashboard-box">
                <div className="row align-items-center m-b-0">
                  <div className="col">
                    <h6 className="m-b-5 text-white">Purchase Orders</h6>
                    <h3 className="m-b-0 text-white">{openPoCount}</h3>
                  </div>
                  <div className="col-auto icon-col">
                    <img src={PurchaseOrderIcon} height="48" alt="Purchase Orders icon" />
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </div>

        {currentVendor.VendorType === 'Warehouse' && (
          <div className={showFeedbackSection ? 'col-xl-3 col-md-3' : 'col-xl-4 col-md-4'}>
            <Link to={'/transfer-orders?range=none'}>
              <div className="card prod-p-card bg-c-green">
                <div className="card-body dashboard-box">
                  <div className="row align-items-center m-b-0">
                    <div className="col">
                      <h6 className="m-b-5 text-white">Transfer Orders</h6>
                      <h3 className="m-b-0 text-white">{TOCount}</h3>
                    </div>
                    <div className="col-auto icon-col">
                      <img src={TransferOrderIcon} height="48" alt="Transfer Orders icon" />
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        )}

        <div
          className={
            currentVendor.VendorType === 'Warehouse'
              ? showFeedbackSection
                ? 'col-xl-3 col-md-3'
                : 'col-xl-4 col-md-4'
              : 'col-xl-6 col-md-6'
          }
        >
          <Link
            to={
              '/purchase-orders?range=none&skipPaginate=false&vendorPortalStatus=Confirmation+Sent+to+Vendor&pageSize=10&currentPage=1'
            }
          >
            <div className="card prod-p-card bg-c-yellow">
              <div className="card-body dashboard-box">
                <div className="row align-items-center m-b-0">
                  <div className="col">
                    <h6 className="m-b-5 text-white">Pending POs</h6>
                    <h3 className="m-b-0 text-white">{pendingPos}</h3>
                  </div>
                  <div className="col-auto icon-col">
                    <img src={PendingOrderIcon} height="48" alt="Purchase Orders icon" />
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </div>

        {showFeedbackSection && currentVendor.VendorType === 'Warehouse' && (
          <div
            className={
              currentVendor.VendorType === 'Warehouse' ? 'col-xl-3 col-md-3' : 'col-xl-6 col-md-6'
            }
          >
            {can_approve_po ? (
              <Link to={'/feedback-orders?range=none'}>
                <div className="card prod-p-card bg-c-red">
                  <div className="card-body dashboard-box">
                    <div className="row align-items-center m-b-0">
                      <div className="col">
                        <h6 className="m-b-5 text-white">Feedback Orders</h6>
                        <h3 className="m-b-0 text-white">{pendingFeedbackLines}</h3>
                      </div>
                      <div className="col-auto icon-col">
                        <i className="feather icon-repeat text-white d-block f-32"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ) : (
              <div className="card prod-p-card bg-c-red">
                <div className="card-body">
                  <div className="row align-items-center m-b-0">
                    <div className="col">
                      <h6 className="m-b-5 text-white">Feedback Orders</h6>
                      <h3 className="m-b-0 text-white">{pendingFeedbackLines}</h3>
                    </div>
                    <div className="col-auto icon-col">
                      <i className="feather icon-repeat text-white d-block f-32"></i>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="row" style={{ minHeight: '25vh', maxHeight: '25vh' }}>
        <div className={feeds.length > 0 ? 'col-xl-7' : 'col-xl-12'}>
          <div className="card h-100">
            <div className="card-header">
              <h5>Orders</h5>
            </div>
            <div className="card-body chart">
              <ResponsiveContainer width="100%" height="92%">
                <BarChart
                  data={chartData}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="PO" fill="#479DFF" />

                  {currentVendor.VendorType === 'Warehouse' && <Bar dataKey="TO" fill="#4CDDC0" />}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        {feeds.length > 0 && (
          <div className="col-xl-5">
            <div className="card  feed-card  h-100">
              <div className="card-header">
                <h5>Feeds</h5>
              </div>
              <div className="feed-scroll">
                <div className="card-body">{feedsContent}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default Dashboard;
